import * as THREE from 'three';
import { customMaterial } from 'lib/mapper';
import React, { useEffect, useState, useMemo } from 'react';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { default_texture } from 'components/base/images';

interface MeshOBJProps {
  url?: string;
  fileData?: ArrayBuffer | File | Blob | Uint8Array;
  textureUrl?: string;
  textureFileData?: ArrayBuffer;
  extension: string;
  isModelFileLoadingProgress: {
    value: boolean;
    onChange: (e: boolean) => void;
    setValue: React.Dispatch<boolean>;
  };
  onModelFile: (mesh: THREE.Mesh) => void;
}

const MeshOBJ: React.FC<MeshOBJProps> = ({
  url,
  fileData,
  textureUrl,
  textureFileData,
  extension,
  isModelFileLoadingProgress,
  onModelFile,
}): any => {
  const [geometry, setGeometry] = useState<THREE.BufferGeometry | null>(null);
  const [texture, setTexture] = useState<THREE.Texture | null>(null);

  useEffect(() => {
    const loadGeometry = async () => {
      try {
        const loader = new OBJLoader();
        let result: THREE.Group;

        if (fileData) {
          let arrayBuffer: ArrayBuffer;
          
          // fileData 타입에 따라 ArrayBuffer로 변환
          if (fileData instanceof ArrayBuffer) {
            arrayBuffer = fileData;
          } else if (fileData instanceof File || fileData instanceof Blob) {
            arrayBuffer = await fileData.arrayBuffer();
          } else if (fileData instanceof Uint8Array) {
            arrayBuffer = fileData.buffer.slice(fileData.byteOffset, fileData.byteOffset + fileData.byteLength);
          } else {
            console.error('fileData is not a valid type:', typeof fileData, fileData);
            return;
          }
          
          const decoder = new TextDecoder('utf-8');
          const objString = decoder.decode(arrayBuffer);
          result = loader.parse(objString);
        } else if (url) {
          result = await new Promise((resolve, reject) => {
            loader.load(url, resolve, undefined, reject);
          });
        } else {
          return;
        }

        let loadedGeometry: THREE.BufferGeometry | null = null;
        result.traverse((child: THREE.Object3D) => {
          if ((child as THREE.Mesh).isMesh) {
            loadedGeometry = (child as THREE.Mesh).geometry;
          }
        });

        if (loadedGeometry) {
          setGeometry(loadedGeometry);
        }
      } catch (error) {
        console.error('Error loading OBJ geometry:', error);
      }
    };

    loadGeometry();
  }, [url, fileData]);

  useEffect(() => {
    const loadTexture = async () => {
      try {
        const loader = new THREE.TextureLoader();
        let loadedTexture: THREE.Texture;

        if (textureFileData) {
          const blob = new Blob([textureFileData]);
          const textureDataUrl = URL.createObjectURL(blob);
          loadedTexture = await new Promise((resolve, reject) => {
            loader.load(textureDataUrl, (tex) => {
              URL.revokeObjectURL(textureDataUrl);
              resolve(tex);
            }, undefined, reject);
          });
        } else if (textureUrl) {
          loadedTexture = await new Promise((resolve, reject) => {
            loader.load(textureUrl, resolve, undefined, reject);
          });
        } else {
          loadedTexture = await new Promise((resolve, reject) => {
            loader.load(default_texture, resolve, undefined, reject);
          });
        }

        setTexture(loadedTexture);
      } catch (error) {
        console.error('Error loading texture:', error);
        const loader = new THREE.TextureLoader();
        loader.load(default_texture, setTexture);
      }
    };

    loadTexture();
  }, [textureUrl, textureFileData]);

  const material = useMemo(() => {
    if (!texture) return null;
    
    return new THREE.ShaderMaterial({
      ...customMaterial,
      uniforms: {
        ...THREE.UniformsUtils.clone(customMaterial.uniforms),
        textureMap: { value: texture },
        isTexture: { value: !!textureUrl || !!textureFileData },
        isVertexColor: { value: false },
        extension: { value: extension },
      },
    });
  }, [texture, extension, textureUrl, textureFileData]);

  return (
    !!material &&
    !!geometry && (
      <mesh
        ref={onModelFile}
        castShadow
        receiveShadow
        geometry={geometry}
        material={material}
        renderOrder={-1}
      />
    )
  );
};

export default MeshOBJ;
